import React, { Fragment, useMemo, type JSX } from 'react';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import BuildsNoDataPlaceholder from '@resources/js/components/BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import useBuildsRotationFacets from '@resources/js/contexts/useBuildsRotationFacets';
import { ThroughputBreakdownHiddenColumnsProvider } from '@resources/js/contexts/useThroughputBreakdownHiddenColumns';
import ThroughputBreakdown, {
  type ThroughputBreakdownViewModel,
} from '@resources/js/components/ThroughputBreakdown/ThroughputBreakdown';
import { type LinkWrapper } from '@resources/js/components/LinkWrapper/LinkWrapper';

export type BuildsThroughputSectionViewModel = {
  title: string;
  description: string;
  breakdowns: Array<
    ThroughputBreakdownViewModel & {
      facetId: string;
    }
  >;
  showAllColumnsLabel: string;
  LinkWrapper: LinkWrapper;
};

export default function BuildsThroughputSection(
  props: BuildsThroughputSectionViewModel,
): JSX.Element | null {
  if (!props.title) {
    return null;
  }

  return (
    <ThroughputBreakdownHiddenColumnsProvider>
      <BuildsThroughputSectionInner {...props} />
    </ThroughputBreakdownHiddenColumnsProvider>
  );
}

function BuildsThroughputSectionInner({
  title,
  description,
  breakdowns,
  LinkWrapper,
}: BuildsThroughputSectionViewModel) {
  const { facet } = useBuildsRotationFacets();
  const facetToFilterTo = facet?.value ?? breakdowns[0]?.facetId;
  const breakdownsForFacet = useMemo(
    () => breakdowns.filter((x) => x.facetId === facetToFilterTo),
    [breakdowns, facetToFilterTo],
  );

  if (!breakdownsForFacet.length) {
    return (
      <VerticalContent className='builds-throughput-section'>
        <EnhancedMarkdown
          markdown={`
# ${title}

${description}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        <BuildsNoDataPlaceholder />
      </VerticalContent>
    );
  }

  return (
    <VerticalContent className='builds-throughput-section'>
      <EnhancedMarkdown
        markdown={`
# ${title}

${description}
`}
        overrides={buildsEnhancedMarkdownOverrides}
      />
      {breakdownsForFacet.map((breakdown) => (
        <Fragment key={breakdown.title + breakdown.facetId}>
          <ThroughputBreakdown
            {...breakdown}
            limit={10}
            LinkWrapper={LinkWrapper}
          />
        </Fragment>
      ))}
    </VerticalContent>
  );
}
